/**
 * Painel reutilizável de gestão de Webhooks Externos.
 *
 * Usado em Settings > Integrações > Webhooks Externos. O CRUD canônico fica
 * aqui — Automations.tsx tem a mesma UI por compat (mesmos endpoints tRPC),
 * mas Configurações é onde o usuário espera encontrar essa configuração.
 *
 * URL pública dos webhooks: `POST /api/webhooks/in/:urlToken` (definida em
 * server/webhooks/incoming-http.ts). A UI antiga em Automations.tsx usava
 * `/api/webhooks/:token` sem o `/in/` — endpoint inexistente; aqui usamos
 * a URL correta.
 */

import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Webhook,
  Plus,
  Pencil,
  Trash2,
  Copy,
  ArrowRight,
  Search,
  RefreshCw,
  Check,
  X,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";

function buildWebhookUrl(token: string): string {
  const base = typeof window !== "undefined" ? window.location.origin : "";
  return `${base}/api/webhooks/in/${token}`;
}

// Campos canônicos suportados pelo processor (server/webhooks/incoming-http.ts).
// Mantemos a lista sincronizada com `ResolvedContactFields`.
const CANONICAL_DEST_OPTIONS: Array<{ value: string; label: string; help: string }> = [
  { value: "phone", label: "Telefone (phone)", help: "Chave de identidade do contato. Obrigatório se o contato é novo." },
  { value: "name", label: "Nome (name)", help: "Nome de exibição." },
  { value: "email", label: "E-mail (email)", help: "Cifrado em repouso (AES-256-GCM)." },
  { value: "companyName", label: "Empresa (companyName)", help: "Nome da empresa do contato." },
];

type MappingRow = {
  /** Path no payload — suporta dot-notation: "data.customer.email" */
  from: string;
  /** Campo destino — canônico (phone/name/email/companyName) ou nome custom */
  to: string;
  /** Quando true, `to` vira custom field; quando false, é canônico */
  isCustom: boolean;
};

const EMPTY_ROW: MappingRow = { from: "", to: "phone", isCustom: false };

export default function WebhooksManagerPanel() {
  const utils = trpc.useUtils();
  const { data: webhooksList = [], isLoading } = trpc.webhooks.list.useQuery();
  const { data: flowsList = [] } = trpc.flows.list.useQuery({ channel: "whatsapp" });

  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [form, setForm] = useState<{
    name: string;
    flowId: string;
    isActive: boolean;
    mappingRows: MappingRow[];
  }>({
    name: "",
    flowId: "",
    isActive: true,
    mappingRows: [],
  });

  const editingWebhook =
    editingId !== null ? webhooksList.find((w) => w.id === editingId) : null;

  const createMut = trpc.webhooks.create.useMutation({
    onSuccess: () => {
      utils.webhooks.list.invalidate();
      toast.success("Webhook criado");
      close();
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });
  const updateMut = trpc.webhooks.update.useMutation({
    onSuccess: () => {
      utils.webhooks.list.invalidate();
      toast.success("Webhook atualizado");
      close();
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });
  const deleteMut = trpc.webhooks.delete.useMutation({
    onSuccess: (_data, vars) => {
      utils.webhooks.list.invalidate();
      // NU-4 — Se o form de edição está aberto para o webhook deletado,
      // fecha (evita update contra linha inexistente).
      if (vars && (vars as { id: number }).id === editingId) {
        close();
      }
      toast.success("Webhook removido");
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });
  const toggleMut = trpc.webhooks.toggle.useMutation({
    onSuccess: () => utils.webhooks.list.invalidate(),
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });
  const rotateMut = trpc.webhooks.rotate.useMutation({
    onSuccess: () => {
      utils.webhooks.list.invalidate();
      toast.success("Token rotacionado. Antigo aceito por 7 dias.");
    },
    // M-2 — Faltava onError; rotação falhada ficava silenciosa.
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  function close() {
    setShowForm(false);
    setEditingId(null);
    setForm({ name: "", flowId: "", isActive: true, mappingRows: [] });
  }

  function openEdit(wh: {
    id: number;
    name: string;
    flowId: number | null;
    isActive: boolean;
    fieldMapping: Record<string, string> | null;
  }) {
    setEditingId(wh.id);
    const canonical = new Set(CANONICAL_DEST_OPTIONS.map((o) => o.value));
    const rows: MappingRow[] = wh.fieldMapping
      ? Object.entries(wh.fieldMapping).map(([from, to]) => ({
          from,
          to,
          isCustom: !canonical.has(to),
        }))
      : [];
    setForm({
      name: wh.name,
      flowId: wh.flowId ? String(wh.flowId) : "",
      isActive: wh.isActive,
      mappingRows: rows,
    });
    setShowForm(true);
  }

  function save() {
    if (!form.name.trim()) {
      toast.error("Nome é obrigatório");
      return;
    }
    // Constrói o Record<string, string> a partir das linhas, ignorando vazias.
    let fieldMapping: Record<string, string> | null = null;
    const validRows = form.mappingRows.filter(
      (r) => r.from.trim() && r.to.trim(),
    );
    // M-3 — Detecta duplicates de `from`. Sem isso, Record sobrescreve
    // silenciosamente o destino anterior e o usuário não percebe.
    const seenFrom = new Set<string>();
    for (const r of validRows) {
      const key = r.from.trim();
      if (seenFrom.has(key)) {
        toast.error(
          `Caminho "${key}" duplicado no mapeamento. Cada caminho do payload só pode apontar para um campo.`,
        );
        return;
      }
      seenFrom.add(key);
    }
    if (validRows.length > 0) {
      fieldMapping = {};
      for (const r of validRows) {
        fieldMapping[r.from.trim()] = r.to.trim();
      }
    }
    const payload = {
      name: form.name.trim(),
      flowId: form.flowId ? Number(form.flowId) : null,
      isActive: form.isActive,
      fieldMapping,
    };
    if (editingId !== null) {
      updateMut.mutate({ id: editingId, ...payload });
    } else {
      createMut.mutate(payload);
    }
  }

  // ── Helpers de manipulação do mapeamento ──────────────────────────────
  function addRow() {
    setForm((f) => ({ ...f, mappingRows: [...f.mappingRows, { ...EMPTY_ROW }] }));
  }
  function removeRow(idx: number) {
    setForm((f) => ({
      ...f,
      mappingRows: f.mappingRows.filter((_, i) => i !== idx),
    }));
  }
  function updateRow(idx: number, patch: Partial<MappingRow>) {
    setForm((f) => ({
      ...f,
      mappingRows: f.mappingRows.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    }));
  }
  /**
   * Sugere mapeamento básico (phone/name/email) — útil para integrações
   * comuns onde o payload já vem com esses nomes.
   */
  function applySuggestion() {
    setForm((f) => ({
      ...f,
      mappingRows: [
        { from: "phone", to: "phone", isCustom: false },
        { from: "name", to: "name", isCustom: false },
        { from: "email", to: "email", isCustom: false },
      ],
    }));
  }

  function copyUrl(id: number, token: string) {
    navigator.clipboard.writeText(buildWebhookUrl(token));
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
    toast.success("URL copiada");
  }

  const filtered = webhooksList.filter(
    (wh) => !search || wh.name.toLowerCase().includes(search.toLowerCase()),
  );
  const totalReq = webhooksList.reduce((s, wh) => s + wh.requestCount, 0);
  const totalLimit = webhooksList.reduce((s, wh) => s + wh.requestLimit, 0) || 25000;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm text-muted-foreground max-w-2xl">
          URLs públicas que sistemas externos (Zapier, Make, formulários, API
          própria) podem chamar para disparar fluxos do LeadConversa. Cada
          webhook é vinculado a um fluxo.
        </p>
        <Button onClick={() => setShowForm(true)} className="gap-2 shrink-0">
          <Plus className="w-4 h-4" />
          Novo webhook
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          Requisições: {totalReq.toLocaleString()}/{totalLimit.toLocaleString()}
        </span>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="h-20 bg-muted animate-pulse rounded-xl" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed border-2 border-border/50">
          <CardContent className="flex flex-col items-center justify-center py-10 gap-3">
            <Webhook className="w-10 h-10 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground text-center">
              Nenhum webhook configurado.
            </p>
            <Button onClick={() => setShowForm(true)} variant="outline" className="gap-2">
              <Plus className="w-4 h-4" />
              Criar primeiro webhook
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((wh) => {
            const flow = flowsList.find((f) => f.id === wh.flowId);
            const usagePct = wh.requestLimit > 0
              ? Math.round((wh.requestCount / wh.requestLimit) * 100)
              : 0;
            return (
              <Card
                key={wh.id}
                className={`transition-all ${wh.isActive ? "border-primary/30 bg-primary/5" : ""}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold">{wh.name}</p>
                        {wh.isActive ? (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full border border-green-200">
                            Ativo
                          </span>
                        ) : (
                          <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full border">
                            Inativo
                          </span>
                        )}
                        <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full border border-blue-200">
                          {wh.requestCount.toLocaleString()} req ({usagePct}%)
                        </span>
                        {wh.hasHmacSecret && (
                          <span className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full border border-purple-200">
                            HMAC ativo
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                        <code className="text-xs bg-muted px-2 py-1 rounded font-mono truncate max-w-md">
                          {buildWebhookUrl(wh.urlToken)}
                        </code>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => copyUrl(wh.id, wh.urlToken)}
                          title="Copiar URL"
                        >
                          {copiedId === wh.id ? (
                            <Check className="h-3 w-3 text-green-600" />
                          ) : (
                            <Copy className="h-3 w-3" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          title="Rotacionar token (gera nova URL; antiga aceita por 7 dias)"
                          onClick={() => {
                            if (
                              confirm(
                                "Rotacionar token? O sistema externo precisará atualizar a URL nos próximos 7 dias (grace period).",
                              )
                            ) {
                              rotateMut.mutate({
                                id: wh.id,
                                gracePeriodHours: 168,
                              });
                            }
                          }}
                        >
                          <RefreshCw className="h-3 w-3" />
                        </Button>
                      </div>
                      {flow && (
                        <div className="flex items-center gap-1.5 mt-2 text-xs text-muted-foreground">
                          <ArrowRight className="h-3 w-3" />
                          <span>
                            Fluxo: <strong className="text-foreground">{flow.name}</strong>
                          </span>
                        </div>
                      )}
                      {wh.fieldMapping && Object.keys(wh.fieldMapping).length > 0 && (
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                          {Object.entries(wh.fieldMapping).map(([key, val]) => (
                            <span
                              key={key}
                              className="text-xs bg-orange-50 text-orange-700 px-2 py-0.5 rounded-full border border-orange-200"
                            >
                              {key} → {val}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => openEdit(wh as any)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Switch
                        checked={wh.isActive}
                        onCheckedChange={(checked) =>
                          toggleMut.mutate({ id: wh.id, isActive: checked })
                        }
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive hover:text-destructive"
                        onClick={() => {
                          if (confirm("Remover este webhook?"))
                            deleteMut.mutate({ id: wh.id });
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit dialog */}
      <Dialog open={showForm} onOpenChange={(o) => !o && close()}>
        <DialogContent className="w-[95vw] max-w-md mx-2 sm:mx-0">
          <DialogHeader>
            <DialogTitle>{editingWebhook ? "Editar" : "Novo"} webhook</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Nome *</Label>
              <Input
                placeholder="Ex: Integração Hotmart, Webhook Stripe"
                value={form.name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, name: e.target.value }))
                }
                className="mt-1"
              />
            </div>
            {editingWebhook && (
              <div>
                <Label>URL do webhook</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    readOnly
                    value={buildWebhookUrl(editingWebhook.urlToken)}
                    className="font-mono text-xs"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    className="shrink-0"
                    onClick={() => copyUrl(editingWebhook.id, editingWebhook.urlToken)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Envie POST para esta URL com Content-Type: application/json
                </p>
              </div>
            )}
            <div>
              <Label>Fluxo a acionar</Label>
              <Select
                value={form.flowId || "_none"}
                onValueChange={(v) =>
                  setForm((f) => ({ ...f, flowId: v === "_none" ? "" : v }))
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecionar fluxo…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Nenhum</SelectItem>
                  {flowsList.map((f) => (
                    <SelectItem key={f.id} value={String(f.id)}>
                      {f.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Mapeamento de campos — editor visual */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Mapeamento de campos</Label>
                {form.mappingRows.length === 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs gap-1"
                    onClick={applySuggestion}
                  >
                    <Sparkles className="h-3 w-3" />
                    Sugerir padrão
                  </Button>
                )}
              </div>

              {form.mappingRows.length === 0 ? (
                <div className="rounded-md border border-dashed p-3 text-xs text-muted-foreground bg-muted/30">
                  Sem mapeamento o sistema tenta detectar automaticamente os
                  campos do payload (<code className="font-mono">phone</code>,{" "}
                  <code className="font-mono">name</code>,{" "}
                  <code className="font-mono">email</code>,{" "}
                  <code className="font-mono">companyName</code>, ou variações
                  PT-BR como <code className="font-mono">telefone</code>,{" "}
                  <code className="font-mono">nome</code>,{" "}
                  <code className="font-mono">empresa</code>). Adicione mapeamento
                  apenas se o payload externo usa nomes diferentes (ex:{" "}
                  <code className="font-mono">data.customer.email</code>).
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <div className="grid grid-cols-[1fr_auto_1fr_auto] items-center gap-2 text-xs text-muted-foreground">
                    <span>Caminho no payload</span>
                    <span />
                    <span>Campo destino no contato</span>
                    <span />
                  </div>
                  {form.mappingRows.map((row, idx) => (
                    <div
                      key={idx}
                      className="grid grid-cols-[1fr_auto_1fr_auto] items-center gap-2"
                    >
                      <Input
                        placeholder="data.customer.email"
                        value={row.from}
                        onChange={(e) => updateRow(idx, { from: e.target.value })}
                        className="font-mono text-xs"
                      />
                      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      {row.isCustom ? (
                        <Input
                          placeholder="meu_campo_custom"
                          value={row.to}
                          onChange={(e) =>
                            updateRow(idx, { to: e.target.value })
                          }
                          className="font-mono text-xs"
                        />
                      ) : (
                        <Select
                          value={row.to}
                          onValueChange={(v) => {
                            if (v === "__custom__") {
                              updateRow(idx, { isCustom: true, to: "" });
                            } else {
                              updateRow(idx, { isCustom: false, to: v });
                            }
                          }}
                        >
                          <SelectTrigger className="text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CANONICAL_DEST_OPTIONS.map((o) => (
                              <SelectItem key={o.value} value={o.value}>
                                {o.label}
                              </SelectItem>
                            ))}
                            <SelectItem value="__custom__">
                              + Campo customizado…
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                      <div className="flex items-center gap-1">
                        {row.isCustom && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            title="Voltar para campo canônico"
                            onClick={() =>
                              updateRow(idx, { isCustom: false, to: "phone" })
                            }
                          >
                            <RefreshCw className="h-3 w-3" />
                          </Button>
                        )}
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive hover:text-destructive"
                          onClick={() => removeRow(idx)}
                          title="Remover linha"
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addRow}
                className="mt-2 gap-1.5 h-8"
              >
                <Plus className="h-3.5 w-3.5" />
                {form.mappingRows.length === 0
                  ? "Adicionar primeiro mapeamento"
                  : "Adicionar campo"}
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={form.isActive}
                onCheckedChange={(checked) =>
                  setForm((f) => ({ ...f, isActive: checked }))
                }
              />
              <Label className="cursor-pointer">Ativo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close}>
              Cancelar
            </Button>
            <Button
              onClick={save}
              disabled={createMut.isPending || updateMut.isPending}
            >
              {createMut.isPending || updateMut.isPending
                ? "Salvando…"
                : editingWebhook
                  ? "Salvar"
                  : "Criar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
